import { useState } from 'react';

interface Image {
  imageUrl: string;
  order: number;
  isCover: boolean;
}

interface ImageGalleryProps {
  images: Image[];
  alt: string;
}

export default function ImageGallery({ images, alt }: ImageGalleryProps) {
  // Sort images by order
  const sortedImages = [...images].sort((a, b) => a.order - b.order);

  // Find cover image index or use first image
  const coverIndex = sortedImages.findIndex((img) => img.isCover);
  const [currentIndex, setCurrentIndex] = useState(coverIndex !== -1 ? coverIndex : 0);

  if (!images || images.length === 0) {
    return (
      <div className="w-full aspect-[16/9] bg-gray-200 rounded-lg flex items-center justify-center">
        <p className="text-gray-500">No images available</p>
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? sortedImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === sortedImages.length - 1 ? 0 : prev + 1));
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full group">
      {/* Main Image */}
      <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-gray-100">
        {/* Blurred Background */}
        <img
          src={sortedImages[currentIndex].imageUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-3xl opacity-30 brightness-110"
          aria-hidden="true"
        />

        {/* Main Image */}
        <img
          src={sortedImages[currentIndex].imageUrl}
          alt={alt}
          className="relative w-full h-full object-contain"
        />

        {/* Navigation Arrows */}
        {sortedImages.length > 1 && (
          <>
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Previous image"
            >
              <svg
                className="w-5 h-5 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Next image"
            >
              <svg
                className="w-5 h-5 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Indicators */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {sortedImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-white w-6' : 'bg-white/60 hover:bg-white/80'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
